import { posthog } from "posthog-js";
import { useEffect, useMemo, useState, useRef } from "react";
import { useRootLoaderData } from "~/utils";
import { ResponsiveDialog } from "./ResponsiveDialog";
import { Button } from "./ui/button";

function cookieConsentGiven() {
  // SSR
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return "undecided";
  }
  const cookieConsent = localStorage.getItem("cookie_consent");
  return !cookieConsent ? "undecided" : cookieConsent;
}

/**
 * The cookie-consent banner bubbles consent across nested CROSS-ORIGIN iframes
 * (deepest app frame up to the dashboard/landing apex). Outbound messages use an
 * explicit targetOrigin instead of `'*'` so consent state is never sent to an
 * arbitrary page embedding us, and inbound messages from fully-foreign origins
 * are rejected.
 *
 * Note the limit of this check: legitimate consent bubbling originates from
 * Moneta-hosted app subdomains (`*.userAppHost`), which is also where published
 * user apps run — so a published app can still post consent/iframe-detection
 * messages to its parent. The stakes are limited to consent/banner UI state.
 *
 * Trusted origins are derived from the same host config the dashboard itself
 * uses (`hosts.userAppHost` / `hosts.landingHost`), so this stays correct across
 * local/dev/staging/prod without hardcoding a literal domain.
 */
function buildTrustedOrigins(hosts: { protocol: string; landingHost: string; userAppHost: string }) {
  if (typeof window === "undefined") return { selfOrigin: "", isTrusted: () => false, parentTarget: "" };

  const selfOrigin = window.location.origin;
  // Generated apps live on subdomains of userAppHost (e.g. user-app.<userAppHost>);
  // the dashboard/landing apex lives on landingHost.
  const parentTarget = `${hosts.protocol}://${hosts.landingHost}`;

  const isTrusted = (origin: string): boolean => {
    if (origin === selfOrigin) return true;
    let host: string;
    try {
      host = new URL(origin).host;
    } catch {
      return false;
    }
    return (
      host === hosts.userAppHost ||
      host.endsWith(`.${hosts.userAppHost}`) ||
      host === hosts.landingHost ||
      host.endsWith(`.${hosts.landingHost}`)
    );
  };

  return { selfOrigin, isTrusted, parentTarget };
}

// only show banner for the deepest iframe and bubble up consent to the parent iframe(s)
export function useCookieConsent() {
  const [consentGiven, setConsentGiven] = useState(cookieConsentGiven());
  const [showBanner, setShowBanner] = useState(false);
  const childIframeDetected = useRef(false);
  const { hosts } = useRootLoaderData();

  const { selfOrigin, isTrusted, parentTarget } = useMemo(() => buildTrustedOrigins(hosts), [hosts]);

  // logic for detecting if the cookie banner should be shown
  useEffect(() => {
    // Announce our presence to the parent frame using an explicit target origin.
    window.top?.postMessage({ type: "IFRAME_EXISTS" }, parentTarget);
    setTimeout(() => {
      if (!childIframeDetected.current && consentGiven === "undecided") {
        setShowBanner(true);
      }
    });
  }, [consentGiven, parentTarget]);

  // logic for event handling
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Reject messages from fully-foreign origins (e.g. an arbitrary site
      // embedding the dashboard). Moneta-hosted app subdomains stay trusted
      // because legitimate consent bubbling originates there — see note above.
      if (!isTrusted(event.origin)) return;

      if (event.data?.type === "IFRAME_EXISTS") {
        childIframeDetected.current = true;
        setShowBanner(false);
        return;
      }

      if (event.data?.type === "COOKIE_CONSENT") {
        const { consent } = event.data;
        posthog.set_config({ persistence: consent === "yes" ? "localStorage+cookie" : "memory" });
        localStorage.setItem("cookie_consent", consent);
        setConsentGiven(consent);
        setShowBanner(false);

        if (window.top !== window) {
          window.top?.postMessage({ type: "COOKIE_CONSENT", consent }, parentTarget);
        }
        return;
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [isTrusted, parentTarget]);

  return {
    consentGiven,
    showBanner,
    handleAcceptCookies: () => window.postMessage({ type: "COOKIE_CONSENT", consent: "yes" }, selfOrigin),
    handleDeclineCookies: () => window.postMessage({ type: "COOKIE_CONSENT", consent: "no" }, selfOrigin),
  };
}

export function CookieBanner() {
  const { showBanner, handleAcceptCookies, handleDeclineCookies } = useCookieConsent();

  if (!showBanner) return null;

  return (
    <ResponsiveDialog open={true} onOpenChange={() => {}} showCancel={false}>
      <div className="space-y-4">
        <h2 className="text-primaryText text-lg font-semibold">Cookie Settings</h2>
        <div className="space-y-2">
          <p className="text-secondaryText text-sm">
            We use cookies to enhance your experience and analyze our website&apos;s performance. This data helps us
            improve our services and provide you with better features.
          </p>
          <p className="text-secondaryText text-sm">
            You can choose to accept or decline these cookies. Essential cookies for basic functionality will always be
            enabled.
          </p>
        </div>
        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:gap-3">
          <Button variant="default" size="default" onClick={handleAcceptCookies} className="flex-1">
            Accept All Cookies
          </Button>
          <Button variant="outline" size="default" onClick={handleDeclineCookies} className="flex-1">
            Essential Cookies Only
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
